import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, getDocs } from "firebase/firestore";

import {
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaEye,
  FaFolderOpen,
} from "react-icons/fa";

import { db } from "../../../firebase/firebase";
import ProjectModal from "../../components/ProjectModal/ProjectModal";

import "./Projects.css";

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [sportFilter, setSportFilter] = useState("All");

  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ==========================================
  // LOAD PROJECTS FROM FIREBASE
  // ==========================================

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError("");

      const snapshot = await getDocs(collection(db, "projects"));

      const data = snapshot.docs.map((document) => ({
        id: document.id,
        ...document.data(),
      }));

      setProjects(data);
    } catch (err) {
      console.error("Firebase projects error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  // ==========================================
  // FILTER PROJECTS
  // ==========================================

  const filteredProjects = projects.filter((project) => {
    const title = project.title || "";
    const location = project.location || "";
    const sport = project.sport || "";

    const searchText = search.toLowerCase();

    const matchesSearch =
      title.toLowerCase().includes(searchText) ||
      location.toLowerCase().includes(searchText);

    const matchesSport = sportFilter === "All" || sport === sportFilter;

    return matchesSearch && matchesSport;
  });

  // ==========================================
  // ADD PROJECT
  // ==========================================

  const handleAddProject = () => {
    setEditingProject(null);
    setShowModal(true);
  };

  // ==========================================
  // EDIT PROJECT
  // ==========================================

  const handleEdit = (project) => {
    console.log("EDIT PROJECT:", project);

    setEditingProject(project);
    setShowModal(true);
  };

  // ==========================================
  // DELETE PROJECT
  // ==========================================

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this project?",
    );

    if (!confirmDelete) {
      return;
    }

    try {
      await deleteDoc(doc(db, "projects", id));

      setProjects((previousProjects) =>
        previousProjects.filter((project) => project.id !== id),
      );

      alert("Project deleted successfully.");
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete project.");
    }
  };

  // ==========================================
  // VIEW PROJECT
  // ==========================================

  const handleView = (project) => {
    console.log("VIEW PROJECT:", project);

    alert(`Project: ${project.title || "Untitled Project"}`);
  };

  // ==========================================
  // MODAL CLOSE
  // ==========================================

  const handleModalClose = () => {
    setShowModal(false);
    setEditingProject(null);

    // Reload Firebase data
    loadProjects();
  };

  // ==========================================
  // LOADING
  // ==========================================

  if (loading) {
    return (
      <div className="projects-page">
        <div className="projects-empty">
          <div className="projects-empty-icon">
            <FaFolderOpen />
          </div>

          <h3>Loading Projects...</h3>

          <p>Loading your projects from Firebase.</p>
        </div>
      </div>
    );
  }

  // ==========================================
  // ERROR
  // ==========================================

  if (error) {
    return (
      <div className="projects-page">
        <div className="projects-empty">
          <div className="projects-empty-icon">
            <FaFolderOpen />
          </div>

          <h3>Firebase Error</h3>

          <p>{error}</p>

          <button className="empty-add-button" onClick={loadProjects}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ==========================================
  // UI
  // ==========================================

  return (
    <div className="projects-page">
      {/* ======================================
          HEADER
      ====================================== */}

      <div className="projects-header">
        <div>
          <h2>Projects</h2>

          <p>Manage all your completed sports construction projects.</p>
        </div>

        <button className="add-project-button" onClick={handleAddProject}>
          <FaPlus />
          Add Project
        </button>
      </div>

      {/* ======================================
          TOOLBAR
      ====================================== */}

      <div className="projects-toolbar">
        {/* SEARCH */}

        <div className="project-search">
          <FaSearch />

          <input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* SPORT FILTER */}

        <select
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
          className="sport-filter"
        >
          <option value="All">All Sports</option>

          <option value="Basketball">Basketball</option>

          <option value="Tennis">Tennis</option>

          <option value="Badminton">Badminton</option>

          <option value="Football">Football</option>

          <option value="Volleyball">Volleyball</option>

          <option value="Running Track">Running Track</option>

          <option value="Pickleball">Pickleball</option>

          <option value="Squash">Squash</option>

          <option value="Cricket">Cricket</option>
        </select>
      </div>

      {/* ======================================
          PROJECT COUNT
      ====================================== */}

      <div className="project-count">
        <span>Total Projects:</span>

        <strong>{filteredProjects.length}</strong>
      </div>

      {/* ======================================
          PROJECT GRID
      ====================================== */}

      {filteredProjects.length > 0 ? (
        <div className="projects-grid">
          {filteredProjects.map((project) => (
            <div className="project-card" key={project.id}>
              {/* IMAGE */}

              <div className="project-image">
                {project.coverImage ? (
                  <img
                    src={project.coverImage}
                    alt={project.title || "Project"}
                  />
                ) : (
                  <div className="project-image-placeholder">
                    <FaFolderOpen />
                  </div>
                )}

                {/* FEATURED */}

                {project.featured && (
                  <span className="featured-badge">Featured</span>
                )}
              </div>

              {/* CONTENT */}

              <div className="project-card-content">
                <span className="project-sport">
                  {project.sport || "Sports"}
                </span>

                <h3>{project.title || "Untitled Project"}</h3>

                <p className="project-location">
                  {project.location || "Location"}

                  {project.state ? `, ${project.state}` : ""}
                </p>

                <p className="project-description">
                  {project.shortDescription ||
                    project.description ||
                    "No description available."}
                </p>

                {/* ACTIONS */}

                <div className="project-actions">
                  {/* VIEW */}

                  <button
                    className="view-button"
                    onClick={() => handleView(project)}
                    title="View Project"
                  >
                    <FaEye />
                  </button>

                  {/* EDIT */}

                  <button
                    className="edit-button"
                    onClick={() => handleEdit(project)}
                    title="Edit Project"
                  >
                    <FaEdit />
                  </button>

                  {/* DELETE */}

                  <button
                    className="delete-button"
                    onClick={() => handleDelete(project.id)}
                    title="Delete Project"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ====================================
           EMPTY STATE
        ==================================== */

        <div className="projects-empty">
          <div className="projects-empty-icon">
            <FaFolderOpen />
          </div>

          <h3>No Projects Found</h3>

          <p>You haven't added any projects yet.</p>

          <button className="empty-add-button" onClick={handleAddProject}>
            <FaPlus />
            Add Your First Project
          </button>
        </div>
      )}

      {/* ======================================
          PROJECT MODAL
      ====================================== */}

      {showModal && (
        <ProjectModal
          projectToEdit={editingProject}
          closeModal={handleModalClose}
        />
      )}
    </div>
  );
};

export default Projects;
